/* Copyright (c) 2020-present CloudIO Inc */

import { IoUserProfilePic, RootRequest } from '@cloudio-saas/datasource-types';
import { useStateXValue } from '@cloudio-saas/statex';
import type { Platform } from 'componentregistry/platform/Platform';
import { usePlatform } from 'platform/PlatformHooks';

export const EMPTY_ARRAY = Object.freeze([]);
export const MISSING_STORE_PATH = ['_missing_store'];

export function userProfilePath(userName: string | null | undefined) {
  return ['userProfile', userName || 'MISSING_USER_NAME'];
}

/**
 * Use this hook to get the user profile info.
 * @param userName
 * @returns {IoUserProfilePic} IoUserProfilePic - an object with the user profile info or undefined if the userName is invalid.
 * - apps?: string;
 * - displayName: string;
 * - emailAddress: string;
 * - endDate?: 'DateTime';
 * - location?: string;
 * - orgUid: string;
 * - profileImage?: string;
 * - roles?: string;
 * - startDate: 'DateTime';
 * - userName: string;
 *
 * @example
 * const { displayName, emailAddress, profileImage } = useUserProfile('john.doe');
 */
export function useUserProfile(
  userName: string | null | undefined,
): IoUserProfilePic | undefined {
  UserProfileCache.init(usePlatform());
  UserProfileCache.initializeUserProfile(userName);
  return useStateXValue(userProfilePath(userName), undefined);
}

class UserProfileCache {
  static platform: Platform;

  static promiseCache: Map<string, Map<string, Promise<boolean> | boolean>> =
    new Map();

  static init(platform: Platform) {
    UserProfileCache.platform = platform;
  }

  static dsList: string[] = [];

  static dsListPromise: Promise<boolean> | null = null;

  static destroy = () => {
    UserProfileCache.dsListPromise = null;
    UserProfileCache.promiseCache = new Map();
    UserProfileCache.dsList = [];
  };

  static fetchUserProfile = async (userName: string) => {
    const reqBody: RootRequest<IoUserProfilePic> = {
      IoUserProfilePicAlias: {
        ds: 'IoUserProfilePic',
        query: {
          filter: [{ userName: { is: userName } }],
          limit: 1,
        },
      },
    };
    const [promise] = UserProfileCache.platform.sendRequest(
      {
        type: 'Post',
        data: reqBody,
        system: true,
      },
      { appUid: 'cloudio' },
    );
    const result = await promise;
    if (result.status === 'success') {
      const profiles: IoUserProfilePic[] =
        result.data.IoUserProfilePicAlias?.data ?? [];
      if (profiles.length) {
        UserProfileCache.platform.setter(
          userProfilePath(userName),
          profiles[0],
        );
      } else {
        UserProfileCache.platform.setter(userProfilePath(userName), {
          userName,
        });
      }
    }
  };

  static initializeUserProfile = async (
    userName: string | null | undefined,
  ): Promise<boolean> => {
    if (!userName) return false;
    let map = UserProfileCache.promiseCache.get(userName);
    if (!map) {
      map = new Map();
      UserProfileCache.promiseCache.set(userName, map);
    }
    let status = map.get(userName);
    if (status !== undefined) {
      return status;
    }
    status = new Promise<boolean>((resolve, reject) => {
      UserProfileCache.fetchUserProfile(userName)
        .then(() => {
          resolve(true);
          map?.set(userName, true);
        })
        .catch((e) => {
          reject(e);
        });
    });

    map.set(userName, status);
    return status;
  };

  static getUserProfile = async (
    userName: string,
  ): Promise<IoUserProfilePic | undefined> => {
    await UserProfileCache.initializeUserProfile(userName);
    return UserProfileCache.platform.getter(userProfilePath(userName));
  };
}

export default UserProfileCache;
